import React, { useState } from 'react';
import { Button, Popconfirm, Table } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import MaintenanceCompanyFormModal from './MaintenanceFormModal';
import { 
    useGetMaintenanceCompanies, 
    useAddMaintenanceCompany, 
    useEditMaintenanceCompany, 
    useDeleteMaintenanceCompany 
} from '../../../hooks/useCompanies';

const MaintenanceCompaniesScreen = () => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);

    const { data: companies = [], isLoading } = useGetMaintenanceCompanies();
    const addCompanyMutation = useAddMaintenanceCompany();
    const editCompanyMutation = useEditMaintenanceCompany();
    const deleteCompanyMutation = useDeleteMaintenanceCompany();

    const handleAdd = () => {
        setEditingCompany(null);
        setIsModalVisible(true);
    };

    const handleEdit = (company) => {
        setEditingCompany(company);
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        deleteCompanyMutation.mutate(id);
    };

    const handleSubmit = async (values) => {
        if (editingCompany) {
            editCompanyMutation.mutate(
                { id: editingCompany.id, values },
                {
                    onSuccess: () => setIsModalVisible(false)
                }
            );
        } else {
            addCompanyMutation.mutate(values, {
                onSuccess: () => setIsModalVisible(false)
            });
        }
    };

    const columns = [
        {
            title: 'Company',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            width: 120,
        },
        {
            title: ' Phone',
            dataIndex: 'contactPhone',
            key: 'contactPhone',
            width: 120,
        },
        {
            title: ' Email',
            dataIndex: 'contactEmail',
            key: 'contactEmail',
            width: 130,
        },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
            ellipsis: true,
            width: 180,
        },
        {
            title: 'Contact Person',
            dataIndex: 'contactPerson',
            key: 'contactPerson',
            width: 140,
        },
        {
            title: 'Person Phone',
            dataIndex: 'contactPersonPhone',
            key: 'contactPersonPhone',
            width: 130,
        },
        {
            title: 'Person Email',
            dataIndex: 'contactPersonEmail',
            key: 'contactPersonEmail',
            width: 180,
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 120,
            render: (_, record) => (
                <div className="flex gap-2">
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        type="text"
                        className="text-blue-600 hover:text-blue-800"
                    />
                    <Popconfirm
                        title="Are you sure to delete this maintenance company?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            type="text"
                            loading={deleteCompanyMutation.isLoading}
                        />
                    </Popconfirm>
                </div>
            ),
        },
    ];

    return (
        <div className="">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Maintenance Companies Management</h2>
                <Button
                    type=""
          size="middle"
          className="bg-gradient-to-r text-white from-primary to-primary_hover hover:bg-primary_hover hover:scale-105 hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-lg font-semibold transition-all"
        
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                >
                    Add Company
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={companies}
                loading={isLoading}
                rowKey="id"
                scroll={{ x: 200 }}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} companies`,
                }}
                className="shadow-md rounded-lg"
            />

            <MaintenanceCompanyFormModal
                visible={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onSubmit={handleSubmit}
                editingCompany={editingCompany}
                isLoading={addCompanyMutation.isLoading || editCompanyMutation.isLoading}
            />
        </div>
    );
};

export default MaintenanceCompaniesScreen;